import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BannerService, PublicBanner, BannerPlacement, BannerText } from '../../../core/services/banner.service';

/**
 * #568 — Affichage des bannières marketing sur le frontoffice.
 *
 * Lit la projection publique via BannerService, compte une impression par
 * bannière affichée et un clic sur le CTA. Aucune donnée sensible : la
 * projection `public_banners` ne contient que les champs d'affichage.
 *
 * Usage : <app-marketing-banner placement="homepage"></app-marketing-banner>
 */
@Component({
  selector: 'app-marketing-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="marketing-banners" *ngIf="banners.length">
      <div class="marketing-banner" *ngFor="let b of banners"
           [style.background-image]="b.bannerImageUrl ? 'url(' + b.bannerImageUrl + ')' : null">
        <div class="marketing-banner__body">
          <h3 class="marketing-banner__title" *ngIf="text(b.title)">{{ text(b.title) }}</h3>
          <p class="marketing-banner__subtitle" *ngIf="text(b.subtitle)">{{ text(b.subtitle) }}</p>
          <div class="marketing-banner__content" *ngIf="text(b.body)" [innerHTML]="text(b.body)"></div>
          <a class="marketing-banner__cta" *ngIf="b.ctaLink && text(b.ctaLabel)"
             [href]="b.ctaLink" (click)="onCtaClick(b)" rel="noopener">
            {{ text(b.ctaLabel) }}
          </a>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .marketing-banners { display: flex; flex-direction: column; gap: 1rem; margin: 1.5rem 0; }
    .marketing-banner {
      position: relative; border-radius: 12px; overflow: hidden;
      background-size: cover; background-position: center;
      padding: 1.75rem; color: #fff;
      box-shadow: 0 2px 12px rgba(0,0,0,.12);
      background-color: #1f2937;
    }
    .marketing-banner__body { position: relative; z-index: 1; max-width: 640px; }
    .marketing-banner__title { margin: 0 0 .35rem; font-size: 1.35rem; font-weight: 700; }
    .marketing-banner__subtitle { margin: 0 0 .5rem; opacity: .9; }
    .marketing-banner__content { margin-bottom: .85rem; }
    .marketing-banner__cta {
      display: inline-block; background: #f59e0b; color: #111827;
      padding: .55rem 1.15rem; border-radius: 8px; font-weight: 600;
      text-decoration: none;
    }
    .marketing-banner__cta:hover { background: #fbbf24; }
  `],
})
export class MarketingBannerComponent implements OnInit {
  @Input() placement: BannerPlacement = 'homepage';
  /** Langue d'affichage (fr par défaut ; fallback nl/en). */
  @Input() lang: 'fr' | 'nl' | 'en' = 'fr';

  banners: PublicBanner[] = [];
  private bannerService = inject(BannerService);

  async ngOnInit(): Promise<void> {
    try {
      this.banners = await this.bannerService.getActiveBanners(this.placement);
    } catch (e) {
      console.warn('[MarketingBanner] load failed:', e);
      this.banners = [];
      return;
    }
    // Une impression par bannière réellement affichée (visiteur réel).
    for (const b of this.banners) {
      this.bannerService.trackImpression(b.id, this.placement);
    }
  }

  onCtaClick(b: PublicBanner): void {
    this.bannerService.trackClick(b.id, this.placement);
    // Navigation naturelle via href (pas de preventDefault) — le tracking est
    // best-effort et ne bloque pas le clic.
  }

  text(t?: BannerText): string {
    if (!t) return '';
    return t[this.lang] || t.fr || t.nl || t.en || '';
  }
}
